import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSnackbar } from 'notistack';
import Layout from '../../../../layouts';
import Page from '../../../../components/Page';
import useAuth from '../../../../hooks/useAuth';
import axiosInstance from '../../../../utils/axios';
import { isMaster } from '../../../../utils/perfil';
import { BotaoExportar, Num, CABECALHO_TABELA } from '../../../../components/gps';
import Modal, { BotaoPrimario, BotaoSecundario, Campo, classesEntrada } from '../../../../components/gps/Modal';
import { ResumoExclusao } from '../../../../components/gps/MenuAcoes';
import TituloTela from '../../../../components/gps/TituloTela';

/**
 * Cadastro de feriados - usado no calculo de SLA em dias uteis.
 *
 * Somente MASTER. O guard esta AQUI DENTRO da pagina, nao apenas no menu:
 * esconder o item de menu nao impede acesso por URL direta (foi o problema
 * encontrado em regionais, onde "visualizar" e "editar" compartilham rota e nao
 * havia checagem na tela). O backend tambem valida - esta e a segunda camada.
 *
 * Migrada para o visual novo (shadcn + Tailwind) sem mudanca de contrato: mesmos
 * endpoints, mesmo corpo enviado, mesma regra de uf/municipio por ambito.
 *
 * DUAS MELHORAS sobre a versao MUI, ambas com motivo:
 *
 * 1. Feriado JA PASSADO aparece esmaecido. A lista cresce por ano e, sem essa
 *    distincao, o cadastro de 2024 disputa atencao com o do mes que vem. Quem e
 *    RECORRENTE nao esmaece - esse vale todo ano, entao a data antiga e so a
 *    origem do registro.
 *
 * 2. O contador do rodape conta os feriados que AINDA VAO ACONTECER, nao o total
 *    de linhas. E esse numero que responde "o SLA deste trimestre ja considera
 *    tudo?".
 */

type Feriado = {
  codigo: number;
  data: string;
  descricao: string;
  ambito: 'NACIONAL' | 'ESTADUAL' | 'MUNICIPAL';
  uf: string | null;
  municipio: string | null;
  recorrenteAnual: boolean;
};

const AMBITOS = ['NACIONAL', 'ESTADUAL', 'MUNICIPAL'];

const TOM_AMBITO: Record<string, string> = {
  NACIONAL: 'bg-info-soft text-info',
  ESTADUAL: 'bg-soft text-muted-foreground',
  MUNICIPAL: 'bg-soft text-faint',
};

const GRADE = '120px minmax(200px,2.4fr) 130px minmax(140px,1.2fr) 110px 90px';

FeriadosPage.getLayout = function getLayout(page: React.ReactElement) {
  return <Layout>{page}</Layout>;
};

function formatarDia(valor: string) {
  if (!valor) return '—';
  const partes = String(valor).slice(0, 10).split('-');
  if (partes.length !== 3) return valor;
  return `${partes[2]}/${partes[1]}/${partes[0]}`;
}

/**
 * Comparacao por TEXTO (aaaa-mm-dd), nao por Date. `new Date('2026-09-07')` e
 * interpretado como UTC meia-noite e, num fuso negativo, volta como dia 06 -
 * feriado sumia da lista "a vir" exatamente no dia em que ele importa.
 */
function jaPassou(valor: string) {
  const dia = String(valor ?? '').slice(0, 10);
  if (dia.length !== 10) return false;
  const hoje = new Date();
  const hojeTexto = `${hoje.getFullYear()}-${String(hoje.getMonth() + 1).padStart(2, '0')}-${String(
    hoje.getDate()
  ).padStart(2, '0')}`;
  return dia < hojeTexto;
}

export default function FeriadosPage() {
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useAuth();
  const ehMaster = isMaster(user);

  const [itens, setItens] = useState<Feriado[]>([]);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState('');

  const [aberto, setAberto] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [editando, setEditando] = useState<Feriado | null>(null);

  const [data, setData] = useState('');
  const [descricao, setDescricao] = useState('');
  const [ambito, setAmbito] = useState('NACIONAL');
  const [uf, setUf] = useState('');
  const [municipio, setMunicipio] = useState('');
  const [recorrente, setRecorrente] = useState(false);

  const carregar = useCallback(async () => {
    setCarregando(true);
    setErro('');
    try {
      const res = await axiosInstance.get<Feriado[]>('tickets/feriados/listar');
      setItens(res.data ?? []);
    } catch (e: any) {
      setErro(e?.response?.data?.message || 'Não foi possível carregar os feriados.');
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => {
    if (ehMaster) carregar();
  }, [carregar, ehMaster]);

  const aVir = useMemo(
    () => itens.filter((f) => f.recorrenteAnual || !jaPassou(f.data)).length,
    [itens]
  );

  if (!ehMaster) {
    return (
      <Page title="Feriados">
        <div className="mx-auto w-full max-w-[var(--gps-pagina-max)] px-4 pb-2 pt-2 font-sans text-ink">
          <div className="rounded-lg border border-line bg-danger-soft p-4 text-[12.5px] text-danger">
            Apenas o perfil <strong>Master</strong> pode acessar o cadastro de feriados.
          </div>
        </div>
      </Page>
    );
  }

  const abrirNovo = () => {
    setEditando(null);
    setData('');
    setDescricao('');
    setAmbito('NACIONAL');
    setUf('');
    setMunicipio('');
    setRecorrente(false);
    setAberto(true);
  };

  const abrirEdicao = (feriado: Feriado) => {
    setEditando(feriado);
    setData(String(feriado.data).slice(0, 10));
    setDescricao(feriado.descricao);
    setAmbito(feriado.ambito);
    setUf(feriado.uf ?? '');
    setMunicipio(feriado.municipio ?? '');
    setRecorrente(Boolean(feriado.recorrenteAnual));
    setAberto(true);
  };

  const salvar = async () => {
    if (!data || !descricao.trim()) {
      enqueueSnackbar('Informe a data e a descrição.', { variant: 'warning' });
      return;
    }
    setSalvando(true);
    try {
      const corpo = {
        data,
        descricao: descricao.trim(),
        ambito,
        uf: ambito === 'NACIONAL' ? null : uf.toUpperCase(),
        municipio: ambito === 'MUNICIPAL' ? municipio.trim() : null,
        recorrenteAnual: recorrente,
      };

      if (editando) await axiosInstance.put(`tickets/feriados/${editando.codigo}`, corpo);
      else await axiosInstance.post('tickets/feriados', corpo);

      enqueueSnackbar('Feriado salvo.', { variant: 'success' });
      setAberto(false);
      carregar();
    } catch (e: any) {
      enqueueSnackbar(e?.response?.data?.message || 'Erro ao salvar o feriado.', { variant: 'error' });
    } finally {
      setSalvando(false);
    }
  };

  /* Confirmacao antes de apagar (30/09/2026). A lixeira fica ao lado do lapis e
     apagava num clique - e um feriado a menos muda o prazo dos proximos tickets. As outras telas (APK, usuario, grupo, politica)
     ja confirmavam. */
  const [apagando, setApagando] = useState<Feriado | null>(null);
  const [removendo, setRemovendo] = useState(false);

  const remover = async (feriado: Feriado) => {
    setRemovendo(true);
    try {
      await axiosInstance.delete(`tickets/feriados/${feriado.codigo}`);
      setApagando(null);
      enqueueSnackbar('Feriado removido.', { variant: 'success' });
      carregar();
    } catch (e: any) {
      enqueueSnackbar(e?.response?.data?.message || 'Erro ao remover.', { variant: 'error' });
    } finally {
      setRemovendo(false);
    }
  };

  return (
    <Page title="Feriados">
      <div className="mx-auto w-full max-w-[var(--gps-pagina-max)] px-4 pb-2 pt-2 font-sans text-ink">
        <div className="mb-5 flex flex-wrap items-end justify-between gap-3">
          <div>
            <TituloTela
              descricao={
                <>
Cada data aqui é descontada do prazo de SLA, junto com sábados e domingos. Feriado{' '}
              <strong>recorrente</strong> vale todo ano e não precisa ser recadastrado.
                </>
              }
            >
              Feriados
            </TituloTela>
          </div>

          <div className="flex flex-none items-center gap-2">
            <BotaoExportar
              nomeArquivo="feriados"
              linhas={() =>
                itens.map((f) => ({
                  // `new Date('2026-09-07')` e meia-noite UTC = dia 06 em BRT; a tela ja usava formatarDia.
                  Data: f.data ? formatarDia(f.data) : '',
                  Descricao: f.descricao ?? '',
                  Ambito: f.ambito ?? '',
                  UF: f.uf ?? '',
                  Municipio: f.municipio ?? '',
                  'Recorrente anual': f.recorrenteAnual ? 'Sim' : 'Nao',
                }))
              }
            />
            <button
              type="button"
              onClick={abrirNovo}
              className="h-9 flex-none rounded-lg bg-primary px-4 text-[12.5px] font-bold text-primary-foreground"
            >
              + Novo feriado
            </button>
          </div>
        </div>

        <div className="overflow-hidden rounded-lg border border-primary/25 bg-surface shadow-card">
          <div className="overflow-x-auto">
            <div className="min-w-[820px]">
              <div
                className={CABECALHO_TABELA}
                style={{ gridTemplateColumns: GRADE }}
              >
                <div>Data</div>
                <div>Descrição</div>
                <div>Âmbito</div>
                <div>Local</div>
                <div>Recorrente</div>
                <div className="text-right">Ações</div>
              </div>

              {carregando && (
                <div className="p-8 text-center text-[12.5px] text-muted-foreground">Carregando…</div>
              )}

              {!carregando && erro && <div className="p-8 text-center text-[12.5px] text-danger">{erro}</div>}

              {!carregando && !erro && itens.length === 0 && (
                <div className="p-8 text-center text-[12.5px] text-muted-foreground">
                  Nenhum feriado cadastrado.
                </div>
              )}

              {!carregando &&
                !erro &&
                itens.map((feriado) => {
                  const passado = !feriado.recorrenteAnual && jaPassou(feriado.data);
                  return (
                    <div
                      key={feriado.codigo}
                      className={`grid items-center border-b px-3.5 hover:bg-surface-2 ${
                        passado ? 'opacity-55' : ''
                      }`}
                      style={{
                        gridTemplateColumns: GRADE,
                        borderColor: 'var(--gps-soft)',
                        minHeight: 'var(--gps-row-h)',
                      }}
                    >
                      <Num className="text-[12px] font-semibold">{formatarDia(feriado.data)}</Num>

                      <span className="min-w-0 truncate pr-3 text-[12.5px]">{feriado.descricao}</span>

                      <div>
                        <span
                          className={`inline-flex h-[22px] items-center rounded-md px-2 text-[11px] font-bold tracking-wide ${
                            TOM_AMBITO[feriado.ambito] || 'bg-soft text-muted-foreground'
                          }`}
                        >
                          {feriado.ambito}
                        </span>
                      </div>

                      <span className="min-w-0 truncate text-[12px] text-muted-foreground">
                        {feriado.municipio
                          ? `${feriado.municipio}${feriado.uf ? `/${feriado.uf}` : ''}`
                          : feriado.uf || '—'}
                      </span>

                      <span className="text-[12px] text-muted-foreground">
                        {feriado.recorrenteAnual ? 'todo ano' : '—'}
                      </span>

                      <div className="flex items-center justify-end gap-1">
                        <button
                          type="button"
                          title="Editar feriado"
                          aria-label="Editar feriado"
                          onClick={() => abrirEdicao(feriado)}
                          className="grid h-8 w-8 place-items-center rounded-md text-muted-foreground hover:bg-soft hover:text-ink"
                        >
                          ✎
                        </button>
                        <button
                          type="button"
                          title="Remover feriado"
                          aria-label="Remover feriado"
                          onClick={() => setApagando(feriado)}
                          className="grid h-8 w-8 place-items-center rounded-md text-danger hover:bg-danger-soft"
                        >
                          🗑
                        </button>
                      </div>
                    </div>
                  );
                })}
            </div>
          </div>

          <div className="border-t border-line px-3.5 py-2.5 text-[12px] text-muted-foreground">
            <Num>{itens.length}</Num> cadastrados · <Num>{aVir}</Num> ainda contam para os próximos prazos
          </div>
        </div>
      </div>

      <Modal
        aberto={aberto}
        titulo={editando ? 'Editar feriado' : 'Novo feriado'}
        descricao="A data deixa de contar como dia útil no cálculo do SLA."
        icone={editando ? '✎' : '+'}
        onFechar={() => setAberto(false)}
        rodape={
          <>
            <BotaoSecundario onClick={() => setAberto(false)} disabled={salvando}>
              Cancelar
            </BotaoSecundario>
            <BotaoPrimario onClick={salvar} disabled={salvando || !data || !descricao.trim()}>
              {salvando ? 'Salvando...' : 'Salvar'}
            </BotaoPrimario>
          </>
        }
      >
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Campo rotulo="Data" obrigatorio>
            <input type="date" value={data} onChange={(e) => setData(e.target.value)} className={classesEntrada} />
          </Campo>

          <Campo rotulo="Âmbito">
            <div className="inline-flex h-9 items-center gap-1 rounded-lg border border-line bg-surface-2 p-1">
              {AMBITOS.map((a) => (
                <button
                  key={a}
                  type="button"
                  onClick={() => setAmbito(a)}
                  className={`h-7 rounded-md px-2 text-[11px] font-bold ${
                    ambito === a ? 'bg-surface text-ink shadow-card' : 'text-muted-foreground'
                  }`}
                >
                  {a}
                </button>
              ))}
            </div>
          </Campo>
        </div>

        <Campo rotulo="Descrição" obrigatorio>
          <input
            value={descricao}
            onChange={(e) => setDescricao(e.target.value)}
            maxLength={200}
            className={classesEntrada}
          />
        </Campo>

        {/* UF e município só aparecem quando o âmbito os usa. Campo desabilitado
            visível é ruído: o usuário tenta preencher e não entende por que não
            aceita. O corpo enviado já anulava esses campos — a tela agora
            concorda com o que é enviado. */}
        {ambito !== 'NACIONAL' && (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Campo rotulo="UF" dica="Duas letras, ex.: SP">
              <input
                value={uf}
                onChange={(e) => setUf(e.target.value.toUpperCase().slice(0, 2))}
                maxLength={2}
                className={`${classesEntrada} uppercase`}
              />
            </Campo>

            {ambito === 'MUNICIPAL' && (
              <Campo rotulo="Município">
                <input
                  value={municipio}
                  onChange={(e) => setMunicipio(e.target.value)}
                  maxLength={120}
                  className={classesEntrada}
                />
              </Campo>
            )}
          </div>
        )}

        <label className="flex cursor-pointer items-start gap-2.5 rounded-lg border border-line bg-surface-2 p-3">
          <input
            type="checkbox"
            checked={recorrente}
            onChange={(e) => setRecorrente(e.target.checked)}
            className="mt-0.5 h-3.5 w-3.5 accent-[hsl(var(--primary))]"
          />
          <span>
            <span className="block text-[12px] font-semibold">Repete todo ano</span>
            <span className="block text-[11px] text-muted-foreground">
              Para datas fixas (7 de setembro, Natal). Feriado móvel, como Carnaval, precisa de um
              cadastro por ano.
            </span>
          </span>
        </label>
      </Modal>
      <Modal
        aberto={Boolean(apagando)}
        titulo="Remover feriado?"
        icone="!"
        largura="max-w-[460px]"
        onFechar={() => !removendo && setApagando(null)}
        rodape={
          <>
            <BotaoSecundario onClick={() => setApagando(null)} disabled={removendo}>
              Cancelar
            </BotaoSecundario>
            <button
              type="button"
              onClick={() => apagando && remover(apagando)}
              disabled={removendo}
              className="h-9 rounded-lg bg-danger px-4 text-[12.5px] font-bold text-white disabled:opacity-40"
            >
              {removendo ? 'Removendo...' : 'Remover'}
            </button>
          </>
        }
      >
        {apagando && <ResumoExclusao titulo={apagando.descricao || 'Feriado'} detalhe={apagando.data ? formatarDia(apagando.data) : undefined} extra={<>O prazo dos próximos tickets passa a contar este dia como útil.</>} />}
      </Modal>
    </Page>
  );
}
